import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const imageBlockVariants = {
  hidden: { opacity: 0, x: -48, scale: 0.96 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
};

const contentBlockVariants = {
  hidden: { opacity: 0, x: 48 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const badgeVariants = {
  hidden: { opacity: 0, scale: 0, rotate: -180 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
      delay: 0.7,
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Section « Nouvelle recette » : design amélioré, titre, image avec overlay, carte contenu avec accent
 */
const NewDishSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  return (
    <motion.section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 w-full min-w-0"
      initial={false}
      animate="visible"
      variants={containerVariants}
    >
      {/* Titre de section */}
      <motion.div
        className="text-center mb-10 sm:mb-12 md:mb-14"
        variants={titleVariants}
      >
        <p className="text-white font-semibold text-sm uppercase tracking-widest mb-2">
          {t("featuredLabel")}
        </p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
          {t("newRecipeSectionTitle")}
        </h2>
        <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full mx-auto mt-3" />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-14 items-center">
        {/* Colonne image */}
        <motion.div
          className="relative order-2 lg:order-1"
          variants={imageBlockVariants}
        >
          <motion.div
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden aspect-[4/3] w-full max-w-lg mx-auto shadow-2xl ring-1 ring-white/10"
            whileHover={{
              scale: 1.02,
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
            }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src="https://images.unsplash.com/photo-1547592166-23ac45744acd?w=1000&h=750&fit=crop&q=85"
              alt={t("newRecipeSectionTitle")}
              className="w-full h-full object-cover object-center"
              decoding="async"
              initial={{ scale: 1.15 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            />
            {/* Étoiles sur l'image, style Top recettes */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            <motion.div
              className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 flex items-center gap-2"
              variants={badgeVariants}
            >
              <span
                className="flex items-center gap-1 font-semibold text-white text-sm sm:text-base drop-shadow-md"
                aria-label={t("fiveStarsAria")}
              >
                <span className="text-amber-400" aria-hidden>
                  ★
                </span>
                <span>5.0</span>
              </span>
              <span className="text-white/90 text-xs sm:text-sm drop-shadow-md">
                {t("recommended")}
              </span>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Colonne contenu */}
        <motion.div
          className="relative bg-black/35 sm:bg-white/10 backdrop-blur-none sm:backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl border border-white/20 order-1 lg:order-2 overflow-hidden"
          variants={contentBlockVariants}
        >
          {/* Bandeau accent gauche */}
          <div className="absolute left-0 top-0 bottom-0 w-1 sm:w-1.5 bg-gradient-to-b from-orange-500 to-amber-500 rounded-r-full" />
          <div className="pl-4 sm:pl-5">
            <motion.p
              className="text-white/90 text-xs sm:text-sm font-medium uppercase tracking-wider mb-2"
              variants={itemVariants}
            >
              {t("soupName")}
            </motion.p>
            <motion.h3
              className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-5"
              variants={itemVariants}
            >
              {t("recipeOfMoment")}
            </motion.h3>
            <motion.p
              className="text-white/95 text-sm sm:text-base mb-6 sm:mb-8 max-w-xl leading-relaxed"
              variants={itemVariants}
            >
              {t("newDishDescription")}
            </motion.p>
            <motion.button
              onClick={onViewRecipes}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-full hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                boxShadow: "0 20px 25px -5px rgba(249, 115, 22, 0.3)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{t("seeRecipe")}</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                →
              </motion.span>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default NewDishSection;
